import { useEffect, useId, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { ChevronDown, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Shared look for form labels: small, bold, foreground colour. */
export const labelClass = "text-[13px] leading-5 font-semibold text-foreground";

const infoIconClass =
  "inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-transparent p-0 text-muted-foreground hover:text-primary focus-visible:outline-2 focus-visible:outline-ring data-[state=open]:text-primary";

/**
 * Small (i) button that shows help text on hover, keyboard focus, click or tap.
 * The text is always in the DOM (screen-reader only) and linked with aria-describedby.
 * With `onExample`, the pop-up also has a "See example" button, so a label never needs two icons.
 */
export const HelpTip = ({ label, children, id, onExample, exampleLabel = "See example", className }: {
  label: string; children: ReactNode; id?: string; onExample?: () => void; exampleLabel?: string; className?: string;
}) => {
  const autoId = useId();
  const descriptionId = id ?? `help-${autoId}`;
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const exampleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const show = () => { window.clearTimeout(closeTimer.current); setOpen(true); };
  const close = () => { window.clearTimeout(closeTimer.current); setOpen(false); setPinned(false); };
  const hideSoon = () => {
    if (pinned) return;
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 150);
  };
  const inTip = (node: EventTarget | null) =>
    node instanceof Element && !!node.closest(`[data-help-tip="${descriptionId}"]`);

  return (
    <Popover open={open} onOpenChange={(next) => (next ? show() : close())}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={`About ${label}`} aria-describedby={descriptionId} data-help-tip={descriptionId}
          className={cn(infoIconClass, className)}
          onPointerEnter={(e) => { if (e.pointerType === "mouse") show(); }}
          onPointerLeave={(e) => { if (e.pointerType === "mouse") hideSoon(); }}
          onFocus={show}
          onBlur={(e) => { if (!inTip(e.relatedTarget)) close(); }}
          onClick={(e) => {
            // Handled here instead of by Radix so hover/focus followed by a click keeps the tip open.
            e.preventDefault();
            e.stopPropagation();
            if (pinned) { close(); return; }
            show();
            setPinned(true);
            if (e.detail === 0 && onExample) window.setTimeout(() => exampleRef.current?.focus(), 0);
          }}>
          <Info className="size-3.5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <span id={descriptionId} className="sr-only">{children}</span>
      <PopoverContent data-evo-island data-help-tip={descriptionId} side="top" collisionPadding={8}
        className="flex w-auto max-w-72 flex-col items-start gap-2 p-3 text-xs leading-5"
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Radix moves focus back to the (i) on close, and onFocus would open the tip again, so it never closes.
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={(e) => { if (e.pointerType === "mouse") show(); }}
        onPointerLeave={(e) => { if (e.pointerType === "mouse") hideSoon(); }}>
        <div aria-hidden="true">{children}</div>
        {onExample && (
          <button ref={exampleRef} type="button" onClick={() => { close(); onExample(); }}
            className="bg-transparent p-0 text-xs font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-ring">
            {exampleLabel}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

/** Slim one-line page header: tinted icon, title and a short description. */
export const PageHeader = ({ id, icon, title, description, className }:
  { id: string; icon: ReactNode; title: string; description: string; className?: string }) => (
  <div data-evo-island className={cn("flex items-center gap-3 rounded-xl bg-linear-to-r from-[color-mix(in_oklab,var(--primary)_70%,black)] to-primary px-4 py-2.5 text-primary-foreground shadow-sm dark:from-[color-mix(in_oklab,var(--primary)_40%,black)] dark:to-[color-mix(in_oklab,var(--primary)_65%,black)] dark:text-foreground", className)}>
    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-background/15 [&_svg]:size-4" aria-hidden="true">{icon}</span>
    <div className="min-w-0">
      <h2 id={id} className="text-base font-semibold leading-6">{title}</h2>
      <p className="text-xs opacity-90">{description}</p>
    </div>
  </div>
);

/** A part of a card without its own border: small tinted icon, bold title with an (i) description, then the fields. */
export const SubSection = ({ icon, title, description, className, children, ...props }:
  { icon: ReactNode; title: string; description?: ReactNode } & Omit<ComponentProps<"section">, "title">) => {
  const id = useId();
  return (
    <section aria-labelledby={id} className={cn("flex min-w-0 flex-col gap-3", className)} {...props}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary [&_svg]:size-3.5" aria-hidden="true">
          {icon}
        </span>
        <h3 id={id} className="text-sm font-semibold leading-5">{title}</h3>
        {description && <HelpTip label={title}>{description}</HelpTip>}
      </div>
      {children}
    </section>
  );
};

/** One group of settings: tinted icon box, title, one-line description, then the fields. */
export const SectionCard = ({ icon, title, description, actions, className, children, ...props }:
  { icon: ReactNode; title: ReactNode; description: string; actions?: ReactNode } & Omit<ComponentProps<"section">, "title">) => {
  const id = `section-${(typeof title === "string" ? title : description).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <section aria-labelledby={id} className={cn("min-w-0 rounded-xl border border-border bg-card text-card-foreground shadow-sm", className)} {...props}>
      <header className="flex flex-wrap items-start gap-3 border-b border-border px-5 py-4">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-[18px]" aria-hidden="true">
          {icon}
        </span>
        <div className="min-w-48 flex-1">
          <h3 id={id} className="flex items-center gap-2 text-base font-semibold leading-6">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 max-sm:w-full max-sm:[&>*]:flex-1">{actions}</div>}
      </header>
      <div className="flex flex-col gap-4 p-5">{children}</div>
    </section>
  );
};

export const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-sm text-destructive">{message}</p> : null;

export const Hint = ({ className, ...props }: ComponentProps<"p">) => (
  <p className={cn("text-xs text-muted-foreground", className)} {...props} />
);

export const Required = () => <span className="text-destructive" aria-hidden="true"> *</span>;

/** Small "i" button that opens the example screenshot for a setting. */
export const InfoButton = ({ label, onClick }: { label: string; onClick?: () => void }) =>
  onClick ? (
    <button type="button" aria-label={label} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={infoIconClass}>
      <Info className="size-3.5" aria-hidden="true" />
    </button>
  ) : null;

/** Label (with an (i) for the hint and/or example), the input, then any error. */
export const Field = ({ label, htmlFor, required, hint, hintId, onExample, exampleLabel, error, className, children }: {
  label: ReactNode; htmlFor?: string; required?: boolean; hint?: ReactNode; hintId?: string;
  /** Opens the example screenshot; shown inside the same (i) as the hint. */
  onExample?: () => void; exampleLabel?: string; error?: string;
  className?: string; children: ReactNode;
}) => {
  const name = typeof label === "string" ? label : htmlFor ?? "this setting";
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <div className="flex items-center gap-1">
        <Label htmlFor={htmlFor} className={labelClass}>{label}{required && <Required />}</Label>
        {hint ? (
          <HelpTip label={name} id={hintId} onExample={onExample} exampleLabel={exampleLabel}>{hint}</HelpTip>
        ) : (
          onExample && <InfoButton label={exampleLabel ?? `${name} example`} onClick={onExample} />
        )}
      </div>
      {children}
      <FieldError message={error} />
    </div>
  );
};

export const selectClass =
  "h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm text-foreground shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 [&>option]:bg-popover [&>option]:text-popover-foreground";

/** Native select styled like the Evo inputs (keeps label lookup and form semantics). */
export const NativeSelect = ({ className, ...props }: ComponentProps<"select">) => (
  // Legacy admin forms rely on native change events and option children.
  // eslint-disable-next-line no-restricted-syntax
  <select className={cn(selectClass, className)} {...props} />
);

/** Native checkbox drawn as an Evo switch (keeps `checked` and label lookup for forms and tests). */
export const SwitchInput = ({ className, ...props }: Omit<ComponentProps<"input">, "type">) => (
  <input
    type="checkbox"
    role="switch"
    className={cn(
      "relative h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-input transition-colors outline-none",
      "before:absolute before:top-0.5 before:left-0.5 before:size-4 before:rounded-full before:bg-background before:shadow-sm before:transition-transform before:content-['']",
      "checked:bg-primary checked:before:translate-x-4 dark:bg-input/80 dark:checked:bg-primary",
      "focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  />
);

export const checkboxClass = "size-4 shrink-0 cursor-pointer rounded border-input accent-primary disabled:cursor-not-allowed disabled:opacity-50";

/** A toggle row: icon, title with an (i) description (and optional example), and an Evo switch.
 * `compact` matches the height of an input or select (h-9) so it can sit beside one. */
export const ToggleRow = ({ id, icon, title, description, checked, onChange, onExample, exampleLabel, compact, children }: {
  id: string; icon: ReactNode; title: string; description?: ReactNode; checked: boolean;
  onChange: ComponentProps<"input">["onChange"]; onExample?: () => void; exampleLabel?: string; compact?: boolean; children?: ReactNode;
}) => (
  <div className={cn("flex items-center rounded-lg border border-border bg-card", compact ? "h-9 gap-2 rounded-md px-3" : "gap-3 px-4 py-3")}>
    <span className={cn("flex shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary", compact ? "size-6 [&_svg]:size-3.5" : "size-8 [&_svg]:size-4")}
      aria-hidden="true">{icon}</span>
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <Label htmlFor={id} className={labelClass}>{title}</Label>
      {description ? (
        <HelpTip label={title} onExample={onExample} exampleLabel={exampleLabel}>{description}</HelpTip>
      ) : (
        onExample && <InfoButton label={exampleLabel ?? `${title} example`} onClick={onExample} />
      )}
    </div>
    {children}
    <SwitchInput id={id} checked={checked} onChange={onChange} />
  </div>
);

/** Indented panel for settings that belong to a toggle above it. */
export const SubPanel = ({ title, className, children, ...props }: { title: string } & ComponentProps<"div">) => (
  <div className={cn("flex flex-col gap-4 rounded-lg border border-border border-l-4 border-l-primary bg-muted/40 p-4", className)} {...props}>
    <p className="text-sm font-semibold">{title}</p>
    {children}
  </div>
);

/** SubPanel that folds open and closed. Closed content stays mounted (hidden) so field refs keep working. */
export const FoldPanel = ({ icon, title, summary, open, onOpenChange, hasError, className, children, ...props }: {
  icon: ReactNode; title: string; summary?: ReactNode; open: boolean; onOpenChange: (open: boolean) => void; hasError?: boolean;
} & Omit<ComponentProps<"div">, "title">) => (
  <Collapsible open={open} onOpenChange={onOpenChange}
    className={cn("min-w-0 rounded-lg border border-border border-l-4 border-l-primary bg-muted/40", className)} {...props}>
    <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded-lg bg-transparent px-3 py-2.5 text-left text-foreground hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary [&_svg]:size-3.5" aria-hidden="true">{icon}</span>
      <span className="text-sm font-semibold">{title}</span>
      {hasError && (
        <span className="size-1.5 shrink-0 rounded-full bg-destructive"><span className="sr-only">(has errors)</span></span>
      )}
      {summary && <span className="min-w-0 truncate text-xs text-muted-foreground">· {summary}</span>}
      <ChevronDown className="ml-auto size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
    </CollapsibleTrigger>
    <CollapsibleContent forceMount className="flex flex-col gap-3 border-t border-border p-3 data-[state=closed]:hidden">
      {children}
    </CollapsibleContent>
  </Collapsible>
);

/** Friendly message for an empty list, with an optional action. */
export const EmptyState = ({ icon, title, description, children }:
  { icon: ReactNode; title: string; description: string; children?: ReactNode }) => (
  <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-10 text-center">
    <span className="flex size-10 items-center justify-center rounded-full bg-secondary text-primary [&_svg]:size-5" aria-hidden="true">{icon}</span>
    <p className="font-medium">{title}</p>
    <p className="text-sm text-muted-foreground">{description}</p>
    {children}
  </div>
);

/** Evo-style header for a legacy (react-bootstrap) modal: icon, title, optional description and a close button. */
export const ModalHeader = ({ icon, title, description, onClose, closeDisabled }: {
  icon: ReactNode; title: ReactNode; description?: ReactNode; onClose: () => void; closeDisabled?: boolean;
}) => (
  <div data-evo-island className="flex items-start gap-3 border-b border-border px-6 py-4 text-foreground">
    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-[18px]" aria-hidden="true">
      {icon}
    </span>
    <div className="min-w-0 flex-1">
      <h2 className="text-lg font-semibold leading-7 break-words">{title}</h2>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
    <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
      aria-label="Close dialog" disabled={closeDisabled} onClick={onClose}>
      <X aria-hidden="true" />
    </Button>
  </div>
);

export const ModalFooter = ({ className, ...props }: ComponentProps<"div">) => (
  <div data-evo-island className={cn("flex flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-4", className)} {...props} />
);
